export const sanitizeVariableName = (name: string): string => {
    if (!name) return "";
    return name
        .replace(/&/g, "And")
        .replace(/\//g, "Or")
        .replace(/[^a-zA-Z0-9_\s]/g, "")
        .replace(/\s+(\w)/g, (_, c) => c.toUpperCase())
        .replace(/\s+/g, "");
};

/**
 * Resolves formula subcategory keys: prefer SystemLine (Excel C13), then display name / formula alias.
 */
export function resolveFormulaSubcategoryKey(
    category: string,
    productName: string,
    prefilledValues?: Record<string, any>,
): string {
    const sel = prefilledValues?.[category];
    const candidates = [
        sel?.systemLine,
        productName,
        sel?.value,
        sel?.formulaProductName,
    ].filter(Boolean);
    return candidates[0] || productName;
}

export function buildSelectionsScope(
    prefilledValues?: Record<string, any>,
): Record<string, string> {
    const scope: Record<string, string> = {};
    if (!prefilledValues) return scope;
    Object.entries(prefilledValues).forEach(([prod, data]: [string, any]) => {
        const sanitizedKey = sanitizeVariableName(prod) + "_Selection";
        scope[sanitizedKey] = data.systemLine || data.value;
    });
    return scope;
}

export const RIDGE_VENT_VENTILATION_TYPE = "Ridge Vent";
export const ROOF_VENT_VENTILATION_TYPE = "Roof Vent";

export function isRidgeVentilationType(ventType: string | undefined): boolean {
    return ventType === RIDGE_VENT_VENTILATION_TYPE;
}

export function isRoofVentilationType(ventType: string | undefined): boolean {
    return ventType === ROOF_VENT_VENTILATION_TYPE;
}

export function getBoxVentCount(attributes: any[] | undefined): number {
    return getAttributeQty(attributes, "Box Vents");
}

export function getTurbineVentCount(attributes: any[] | undefined): number {
    return getAttributeQty(attributes, "Turbine Vents");
}

export function getRoofVentCount(attributes: any[] | undefined): number {
    return getBoxVentCount(attributes) + getTurbineVentCount(attributes);
}

/** Sum box + turbine counts into RoofVents for formulas (ridge vent uses Roof Vents LF separately). */
export function applyRoofVentCountScopeMapping(
    scope: Record<string, any>,
    attributes?: any[],
    inspectionScopeKeys?: Set<string>,
): void {
    if (
        isRidgeVentilationType(
            getAttributeValue(attributes, "Ventilation Type"),
        )
    )
        return;

    const box = getBoxVentCount(attributes);
    const turbine = getTurbineVentCount(attributes);

    if (box > 0) {
        scope.BoxVents = box;
        inspectionScopeKeys?.add("BoxVents");
    }
    if (turbine > 0) {
        scope.TurbineVents = turbine;
        inspectionScopeKeys?.add("TurbineVents");
    }

    const total = box + turbine;
    if (total > 0) {
        scope.RoofVents = total;
        inspectionScopeKeys?.add("RoofVents");
    }
}

export function getAttributeValue(
    attributes: any[] | undefined,
    name: string,
): string | undefined {
    const attr = attributes?.find((a) => a.AttributeName === name);
    return attr?.AttributeValue ?? undefined;
}

export function getAttributeQty(
    attributes: any[] | undefined,
    name: string,
): number {
    const attr = attributes?.find((a) => a.AttributeName === name);
    if (attr?.Qty !== null && attr?.Qty !== undefined && attr?.Qty !== "") {
        const num = Number(attr.Qty);
        if (!isNaN(num)) return num;
    }
    return 0;
}

export function getMeasurementLf(
    measurements: any,
    attributes: any[] | undefined,
    measurementType: string,
): number {
    if (Array.isArray(measurements)) {
        const row = measurements.find(
            (m) => m.MeasurementType === measurementType,
        );
        const raw = row?.ActualValue ?? row?.CalculatedValue;
        const num = Number(raw);
        if (!isNaN(num) && num > 0) return num;
    }
    return getAttributeQty(attributes, measurementType);
}

/** Ridge vent LF is entered under Roof Vents in inspection when ventilation type is ridge vent. */
export function getRidgeVentLf(
    measurements: any,
    attributes: any[] | undefined,
): number {
    const ridgeLf = getMeasurementLf(measurements, attributes, "Ridge");
    if (ridgeLf > 0) return ridgeLf;
    if (
        getAttributeValue(attributes, "Ventilation Type") !==
        RIDGE_VENT_VENTILATION_TYPE
    )
        return 0;
    return getAttributeQty(attributes, "Roof Vents");
}

/** Remap identify-products params: ridge vent LF → Ridge, not Roof Vents count. */
/** Map RoofPitch JSON rows into RoofPitch6Square, RoofPitch7Square, … .
 *  `Squares` on each row is stored as square feet (SF); formulas receive squares (SF / 100).
 */
export function applyRoofPitchToScope(value: any) {
    let entries = value;

    const finalValue = {} as any;
    if (typeof value === "string" && value.trim().startsWith("[")) {
        try {
            entries = JSON.parse(value);
        } catch {
            return;
        }
    }

    if (!Array.isArray(entries)) return;

    const byPitch: Record<number, number> = {};

    for (const item of entries) {
        const pitchStr = item?.Pitch;
        const squareFeet = Number(item?.Squares);
        if (!pitchStr || isNaN(squareFeet)) continue;

        const match = String(pitchStr).match(/^(\d+)\s*\/\s*\d+/);
        if (!match) continue;

        const pitchNum = Number(match[1]);
        const squares = squareFeet / 100;
        byPitch[pitchNum] = (byPitch[pitchNum] || 0) + squares;
    }

    for (const [pitch, total] of Object.entries(byPitch)) {
        finalValue[`RoofPitch${pitch}Square`] = total;
    }
    return finalValue;
}

export function applyRidgeVentMeasurementParams(
    params: Record<string, number>,
    attributes?: any[],
): void {
    if (
        getAttributeValue(attributes, "Ventilation Type") !==
        RIDGE_VENT_VENTILATION_TYPE
    )
        return;

    const roofVentLf = params.RoofVents || 0;
    delete params.RoofVents;

    if ((!params.Ridge || params.Ridge <= 0) && roofVentLf > 0) {
        params.Ridge = roofVentLf;
    }
}

/** Map inspection ridge vent LF into Ridge for formulas like `Ridge / 4`. */
export function applyRidgeVentScopeMapping(
    scope: Record<string, any>,
    attributes?: any[],
    inspectionScopeKeys?: Set<string>,
): void {
    if (
        getAttributeValue(attributes, "Ventilation Type") !==
        RIDGE_VENT_VENTILATION_TYPE
    )
        return;

    const roofVentLf = getAttributeQty(attributes, "Roof Vents");
    if ((!scope.Ridge || Number(scope.Ridge) === 0) && roofVentLf > 0) {
        scope.Ridge = roofVentLf;
        inspectionScopeKeys?.add("Ridge");
    }
}
